/**
 * Product cap for the header search field (CSS `max-w-[600px]`). Not a spacing-scale token.
 * Layout mode uses the uncapped slot width against the floor; this constant is the visual cap only.
 */
export const WORKSPACE_HEADER_SEARCH_MAX_WIDTH_PX = 600;

/**
 * Balancing both columns to the wider side takes `|actions - workspaceInfo|` away from the field.
 * Below this width the field stops reading as a search bar, so the header keeps the flex
 * leftover layout narrow viewports already use.
 */
export const WORKSPACE_HEADER_SEARCH_CENTER_MIN_WIDTH_PX = 320;

/** Stay centered through a 1px resize that would otherwise flip the layout. */
export const WORKSPACE_HEADER_SEARCH_CENTER_HYSTERESIS_PX = 16;

export type WorkspaceHeaderSearchMeasurements = {
  actionsWidthPx: number;
  headerWidthPx: number;
  searchSlotPaddingPx: number;
  workspaceInfoWidthPx: number;
};

export type WorkspaceHeaderSearchCenteredLayout = {
  gutterPx: number;
  mode: 'centered';
};

export type WorkspaceHeaderSearchNaturalLayout = {
  mode: 'natural';
};

export type WorkspaceHeaderSearchLayout = WorkspaceHeaderSearchCenteredLayout | WorkspaceHeaderSearchNaturalLayout;

const NATURAL_LAYOUT: WorkspaceHeaderSearchNaturalLayout = { mode: 'natural' };

const roundUpPx = (widthPx: number) => Math.ceil(widthPx);

const getCenteredSearchWidthPx = ({
  gutterPx,
  headerWidthPx,
  searchSlotPaddingPx,
}: {
  gutterPx: number;
  headerWidthPx: number;
  searchSlotPaddingPx: number;
}) => headerWidthPx - gutterPx * 2 - searchSlotPaddingPx;

export const areWorkspaceHeaderSearchLayoutsEqual = (
  left: WorkspaceHeaderSearchLayout,
  right: WorkspaceHeaderSearchLayout,
) => {
  if (left.mode === 'natural' || right.mode === 'natural') {
    return left.mode === right.mode;
  }

  return left.gutterPx === right.gutterPx;
};

/**
 * Equal gutters keep the field on the header center when pages swap right-side buttons.
 * When that gutter would push the field under the floor, return the flex leftover layout instead.
 */
export const getWorkspaceHeaderSearchLayout = (
  measurements: WorkspaceHeaderSearchMeasurements,
  previous: WorkspaceHeaderSearchLayout,
): WorkspaceHeaderSearchLayout => {
  const headerWidthPx = Math.floor(measurements.headerWidthPx);

  if (headerWidthPx <= 0) {
    return NATURAL_LAYOUT;
  }

  const gutterPx = Math.max(roundUpPx(measurements.workspaceInfoWidthPx), roundUpPx(measurements.actionsWidthPx));
  const centeredSearchWidthPx = getCenteredSearchWidthPx({
    gutterPx,
    headerWidthPx,
    searchSlotPaddingPx: measurements.searchSlotPaddingPx,
  });
  const centerMinWidthPx =
    previous.mode === 'centered'
      ? WORKSPACE_HEADER_SEARCH_CENTER_MIN_WIDTH_PX - WORKSPACE_HEADER_SEARCH_CENTER_HYSTERESIS_PX
      : WORKSPACE_HEADER_SEARCH_CENTER_MIN_WIDTH_PX;

  if (centeredSearchWidthPx < centerMinWidthPx) {
    return NATURAL_LAYOUT;
  }

  return { gutterPx, mode: 'centered' };
};
